/**
 * Cognito user-pool sign-in with **SRP** against an app client with no client
 * secret and no hosted UI — the password never leaves the client.
 *
 * `amazon-cognito-identity-js` is the smallest maintained library that does
 * the SRP maths and the PASSWORD_VERIFIER round trip. It is injected as
 * `SrpLib` (`realSrpLib` wires the library), so the callback-to-outcome
 * mapping is tested against a fake. The library keeps tokens in a storage we
 * hand it — `memoryStorage`, which forgets — so persistence stays the caller's.
 *
 * The library reads Node's `global` at import time: a browser bundle must
 * define `global` as `globalThis` (Vite: `define: { global: 'globalThis' }`).
 */
import { AuthenticationDetails, CognitoUser, CognitoUserPool, type ICognitoStorage } from 'amazon-cognito-identity-js';

/**
 * Which user pool and app client to sign in against. The app client must have
 * no client secret and allow `ALLOW_USER_SRP_AUTH` (sign-in) and
 * `ALLOW_REFRESH_TOKEN_AUTH` (renewal).
 */
export type CognitoClientConfig = { readonly region: string; readonly userPoolId: string; readonly clientId: string };

/** The three tokens of a signed-in user, as Cognito issued them. */
export type CognitoTokens = { readonly idToken: string; readonly accessToken: string; readonly refreshToken: string };

/** What the library's session object offers, as far as we read it. */
export type SrpSessionLike = {
  getIdToken(): { getJwtToken(): string };
  getAccessToken(): { getJwtToken(): string };
  getRefreshToken(): { getToken(): string };
};

export type SrpCallbacks = {
  onSuccess: (session: SrpSessionLike) => void;
  onFailure: (err: unknown) => void;
  newPasswordRequired: (userAttributes: unknown, requiredAttributes: unknown) => void;
  // The challenges not supported yet. The library calls these unguarded, so
  // each must exist, or a pool user with MFA meets a TypeError (or a promise
  // that never settles) instead of an explanation.
  mfaRequired: () => void;
  totpRequired: () => void;
  mfaSetup: () => void;
  selectMFAType: () => void;
  customChallenge: () => void;
};

export type SrpUserLike = {
  authenticateUser(details: AuthenticationDetails, callbacks: SrpCallbacks): void;
  completeNewPasswordChallenge(newPassword: string, requiredAttributeData: Record<string, string>, callbacks: SrpCallbacks): void;
};

export type SrpLib = {
  readonly user: (username: string) => SrpUserLike;
  readonly details: (username: string, password: string) => AuthenticationDetails;
};

export type SrpOutcome =
  | { readonly type: 'signed-in'; readonly tokens: CognitoTokens }
  | { readonly type: 'new-password-required'; readonly complete: (newPassword: string) => Promise<SrpOutcome> };

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const message: unknown = Reflect.get(err, 'message');
    if (typeof message === 'string') return message;
  }
  return String(err);
}

function tokensOf(result: SrpSessionLike): CognitoTokens {
  return {
    idToken: result.getIdToken().getJwtToken(),
    accessToken: result.getAccessToken().getJwtToken(),
    refreshToken: result.getRefreshToken().getToken(),
  };
}

/**
 * Sign in with SRP. Resolves with the tokens, or with the new-password
 * challenge (an admin-created user's first sign-in) as a continuation; rejects
 * with Cognito's message (wrong password, unknown user, …) or, for a challenge
 * not supported, a sentence naming it and `appName` ("This account needs TOTP
 * MFA, which <appName> does not support yet. Ask an administrator.").
 */
export function srpSignIn(lib: SrpLib, username: string, password: string, appName: string): Promise<SrpOutcome> {
  const user = lib.user(username);
  const unsupported = (challenge: string): Error =>
    new Error(`This account needs ${challenge}, which ${appName} does not support yet. Ask an administrator.`);
  const settle = (run: (callbacks: SrpCallbacks) => void): Promise<SrpOutcome> =>
    new Promise<SrpOutcome>((resolve, reject) => {
      run({
        onSuccess: (result) => {
          try {
            resolve({ type: 'signed-in', tokens: tokensOf(result) });
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        },
        onFailure: (err) => reject(new Error(errorMessage(err))),
        mfaRequired: () => reject(unsupported('SMS MFA')),
        totpRequired: () => reject(unsupported('TOTP MFA')),
        mfaSetup: () => reject(unsupported('MFA setup')),
        selectMFAType: () => reject(unsupported('MFA type selection')),
        customChallenge: () => reject(unsupported('a custom challenge')),
        newPasswordRequired: () =>
          resolve({
            type: 'new-password-required',
            complete: (newPassword) => settle((callbacks) => user.completeNewPasswordChallenge(newPassword, {}, callbacks)),
          }),
      });
    });
  return settle((callbacks) => user.authenticateUser(lib.details(username, password), callbacks));
}

/** A storage that forgets: the library must not write tokens to localStorage behind its caller's back. */
export function memoryStorage(): ICognitoStorage {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
    removeItem: (key) => {
      data.delete(key);
    },
    clear: () => {
      data.clear();
    },
  };
}

/** The real library, for `config`'s pool and app client, over a forgetting storage. */
export function realSrpLib(config: CognitoClientConfig): SrpLib {
  const storage = memoryStorage();
  const pool = new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId, Storage: storage });
  return {
    user: (username) => new CognitoUser({ Username: username, Pool: pool, Storage: storage }),
    details: (username, password) => new AuthenticationDetails({ Username: username, Password: password }),
  };
}
